// Small pieces of the report screen: caret, multi-select filter, KPI tile,
// filter chips and the toast. Presentation only.
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, X } from "feather-icons-react/build/IconComponents";
import { pct2 } from "./reportFormat";

export const Caret = () => (
  <svg
    className="axr-caret"
    viewBox="0 0 12 12"
    width="10"
    height="10"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    <path d="M3 5l3 3 3-3" />
  </svg>
);

/* ---------------- multi-select filter ---------------- */
export const MultiSelect = ({ id, label, items, value, onChange, openPop, setOpenPop }) => {
  const [q, setQ] = useState("");
  const open = openPop === id;
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const shown = items.filter(
    (it) =>
      !q ||
      String(it.t).toLowerCase().includes(q.toLowerCase()) ||
      String(it.c || "").toLowerCase().includes(q.toLowerCase())
  );
  const n = value.length;
  const single = n === 1 ? items.find((i) => i.v === value[0])?.t : null;

  return (
    <div className={"axr-ms" + (n ? " on" : "")}>
      <button
        type="button"
        className="axr-ms-btn"
        aria-expanded={open}
        onClick={() => setOpenPop(open ? null : id)}
      >
        <span className="axr-lbl">{single || label}</span>
        {n > 0 && <span className="axr-n">{n}</span>}
        <ChevronDown />
      </button>
      {open && (
        <div className="axr-ms-pop">
          <input
            ref={searchRef}
            className="axr-ms-search"
            placeholder={`Filter ${String(label).toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="axr-ms-list">
            {shown.length === 0 && <div className="axr-ms-none">No matches</div>}
            {shown.map((it) => (
              <label className="axr-ms-opt" key={String(it.v)}>
                <input
                  type="checkbox"
                  checked={value.includes(it.v)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...value, it.v]
                        : value.filter((x) => x !== it.v)
                    )
                  }
                />
                <span className="axr-t">{it.t}</span>
                {it.c && <span className="axr-c">{it.c}</span>}
              </label>
            ))}
          </div>
          <div className="axr-ms-foot">
            <button type="button" onClick={() => onChange(items.map((i) => i.v))}>
              Select all
            </button>
            <button type="button" onClick={() => onChange([])}>
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- KPI tile ---------------- */
export const Kpi = ({ label, value, delta, hero, invert }) => {
  let arrow = null;
  if (delta !== null && delta !== undefined && isFinite(delta)) {
    const good = invert ? delta < 0 : delta >= 0;
    arrow = (
      <div className={"axr-kd " + (good ? "up" : "down")}>
        <b>
          {delta >= 0 ? "▲ " : "▼ "}
          {pct2(Math.abs(delta))}%
        </b>{" "}
        vs previous period
      </div>
    );
  }
  return (
    <div className={"axr-card axr-kpi" + (hero ? " hero" : "")}>
      <div className="axr-kk">{label}</div>
      <div className="axr-kv">{value}</div>
      {arrow}
    </div>
  );
};

/* ---------------- active filter chips ---------------- */
export const FilterChips = ({ chips, onClearAll }) => {
  if (!chips.length) return null;
  return (
    <div className="axr-chiprow">
      <span className="axr-ttl">Filters</span>
      {chips.map((c) => (
        <span className="axr-fchip" key={c.label}>
          {c.label} <b>{c.value}</b>
          <button type="button" aria-label="Remove filter" onClick={c.clear}>
            <X />
          </button>
        </span>
      ))}
      <button type="button" className="axr-clearall" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
};

/* ---------------- toast ---------------- */
export const Toast = ({ message }) => (
  <div className={"axr-toast" + (message ? " show" : "")} role="status">
    <span className="ok">✓</span>
    <span>{message}</span>
  </div>
);
